import { useCallback, useRef, useState } from "react";
import { OrderStatus } from "../../../../enums/authStatus";
import { QaFilledBy } from "../../../../enums/qaFilledBy";
import { SecondStpStatus } from "../../../../enums/secondStpStatus";

/**
 * Represents the data structure for a submission summary form.
 * @interface SubmissionSummaryData
 * @property {string} qaFilledBy - Identifier for who filled the QA
 * @property {string} secondStpStatus - The secondary STP status value
 * @property {string} caseStatus - The current case status
 * @property {string} poc - Point of contact identifier
 * @property {string} comments - Additional comments for the submission
 * @property {string} key - Unique identifier for the submission
 * @property {string} callRequired - Delivery tab only: `"yes"` | `"no"` | `""`
 * @property {string} callReason - Delivery tab only: required when `callRequired` is `"yes"`
 */
interface SubmissionSummaryData {
  qaFilledBy: string;
  secondStpStatus: string;
  caseStatus: string;
  poc: string;
  comments: string;
  key: string;
  callRequired: string;
  callReason: string;
}

/**
 * Enum containing the keys used in SubmissionSummaryData.
 * @enum {string}
 */
export enum SubmissionSummaryDataKeys {
  QA_FILLED_BY = "qaFilledBy",
  SECOND_STP_STATUS = "secondStpStatus",
  CASE_STATUS = "caseStatus",
  POC = "poc",
  COMMENTS = "comments",
  KEY = "key",
  CALL_REQUIRED = "callRequired",
  CALL_REASON = "callReason",
}

/**
 * Maps stored/API `non_epa_required` values to yes/no for the compact submission UI.
 *
 * @param raw - Raw value from the order row
 * @returns `"yes"`, `"no"`, or empty string when unset or unrecognized
 */
const normalizeCallRequiredFromRecord = (raw: string | undefined): string => {
  if (raw === undefined || raw === null) return "";
  const v = String(raw).trim().toLowerCase();
  if (v === "yes") return "yes";
  if (v === "no") return "no";
  return "";
};

/**
 * Optional behavior when the submission summary uses a reduced field set (questionnaire / request outcome).
 */
export interface UseSubmissionSummaryDataOptions {
  /**
   * When true, Second STP Status and Case Status are omitted from the form and
   * `second_stp_status` / `response_status` are not sent on save.
   * Validation requires key and POC only.
   */
  hideQaSecondStpAndComments?: boolean;
  /**
   * When true (Delivery tab), Call Required and Call reason are shown and required on save.
   */
  showCallFields?: boolean;
  /** When true, an empty key text field does not block submission. */
  isKeyFieldOptional?: boolean;
}

/**
 * Custom hook to manage submission summary form data with cascading field resets.
 *
 * This hook provides state management for a submission summary form where certain
 * field changes trigger resets of dependent fields:
 * - Changing `qaFilledBy` resets both `secondStpStatus` and `caseStatus`
 * - Changing `secondStpStatus` resets `caseStatus`
 *
 * @param {string} key - Unique identifier for the submission summary
 * @param {Partial<SubmissionSummaryData> | Record<string, string> | null | undefined} initialData - Optional existing row data (snake_case or camelCase) to prefill the form. POC is taken from `poc`, falling back to `assignee_id` for CMM orders.
 * @param {UseSubmissionSummaryDataOptions | undefined} options - Optional flags for compact modal (questionnaire / request outcome)
 * @returns {Object} An object containing:
 * @returns {SubmissionSummaryData} submissionSummaryData - Current form state
 * @returns {(id: string, value: string) => void} handleSubmissionSummaryDataChange - Generic handler for updating any field
 * @returns {(id: string) => void} handleQaFilledByChange - Handler for qaFilledBy changes (resets secondStpStatus and caseStatus)
 * @returns {(id: string) => void} handleSecondStpStatusChange - Handler for secondStpStatus changes (resets caseStatus)
 * @returns {boolean} isFormValid - Whether all required fields are filled
 *
 * @example
 * const {
 *   submissionSummaryData,
 *   handleSubmissionSummaryDataChange,
 *   handleQaFilledByChange,
 *   handleSecondStpStatusChange,
 *   isFormValid
 * } = useSubmissionSummaryData("submission-123");
 */
const getInitialState = (
  key: string,
  initialData?: Partial<SubmissionSummaryData> | Record<string, string> | null,
): SubmissionSummaryData => {
  const defaultState: SubmissionSummaryData = {
    [SubmissionSummaryDataKeys.QA_FILLED_BY]: "",
    [SubmissionSummaryDataKeys.SECOND_STP_STATUS]: "",
    [SubmissionSummaryDataKeys.CASE_STATUS]: "",
    [SubmissionSummaryDataKeys.POC]: "",
    [SubmissionSummaryDataKeys.COMMENTS]: "",
    [SubmissionSummaryDataKeys.KEY]: key || "",
    [SubmissionSummaryDataKeys.CALL_REQUIRED]: "",
    [SubmissionSummaryDataKeys.CALL_REASON]: "",
  };
  if (!initialData) return defaultState;

  const record = initialData as Record<string, string>;
  const isFirstStpOutcome = record.cmm_status === OrderStatus.FIRST_STP_OUTCOME;
  // CMM / insurance-details orders store the assignee Firebase UID on
  // `assignee_id`; STP Delivery rows may already have `poc` set to that UID.
  const resolvedPoc = record.poc || record.assignee_id || "";

  return {
    ...defaultState,
    [SubmissionSummaryDataKeys.KEY]: key,
    [SubmissionSummaryDataKeys.QA_FILLED_BY]:
      record.qaFilledBy ||
      record.qa_filled_by ||
      (isFirstStpOutcome ? QaFilledBy.NOT_NEEDED : ""),
    [SubmissionSummaryDataKeys.SECOND_STP_STATUS]:
      record.secondStpStatus ||
      record.second_stp_status ||
      (isFirstStpOutcome ? SecondStpStatus.FIRST_STP_OUTCOME : ""),
    [SubmissionSummaryDataKeys.CASE_STATUS]:
      record.caseStatus ||
      record.case_status ||
      record.response_status ||
      (isFirstStpOutcome
        ? ((record as Record<string, any>).first_stp_outcome_data?.bucket ?? "")
        : ""),
    [SubmissionSummaryDataKeys.POC]: resolvedPoc,
    [SubmissionSummaryDataKeys.COMMENTS]: record.comments || "",
    [SubmissionSummaryDataKeys.CALL_REQUIRED]: normalizeCallRequiredFromRecord(
      record["non_epa_required"],
    ),
    [SubmissionSummaryDataKeys.CALL_REASON]: record["non_epa_reason"] ?? "",
  };
};

export const useSubmissionSummaryData = (
  key: string,
  initialData?: Partial<SubmissionSummaryData> | Record<string, string> | null,
  options?: UseSubmissionSummaryDataOptions,
) => {
  const hideQaSecondStpAndComments =
    options?.hideQaSecondStpAndComments ?? false;
  const showCallFields = options?.showCallFields ?? false;
  const isKeyFieldOptional = options?.isKeyFieldOptional ?? false;

  const initialStateRef = useRef<SubmissionSummaryData>(
    getInitialState(key, initialData),
  );
  const [submissionSummaryData, setSubmissionSummaryData] =
    useState<SubmissionSummaryData>(() => initialStateRef.current);

  const handleSubmissionSummaryDataChange = useCallback(
    (id: string, value: string) => {
      setSubmissionSummaryData((prev) => ({
        ...prev,
        [id]: value,
      }));
    },
    [],
  );

  const handleQaFilledByChange = useCallback((id: string) => {
    setSubmissionSummaryData((prev) => ({
      ...prev,
      [SubmissionSummaryDataKeys.QA_FILLED_BY]: id,
      [SubmissionSummaryDataKeys.SECOND_STP_STATUS]: "",
      [SubmissionSummaryDataKeys.CASE_STATUS]: "",
    }));
  }, []);

  const handleSecondStpStatusChange = useCallback((id: string) => {
    setSubmissionSummaryData((prev) => ({
      ...prev,
      [SubmissionSummaryDataKeys.SECOND_STP_STATUS]: id,
      [SubmissionSummaryDataKeys.CASE_STATUS]: "",
    }));
  }, []);

  /**
   * Updates call required (yes/no) for the Delivery tab modal.
   *
   * @param value - `"yes"` or `"no"`
   */
  const handleCallRequiredChange = useCallback((value: string) => {
    setSubmissionSummaryData((prev) => ({
      ...prev,
      [SubmissionSummaryDataKeys.CALL_REQUIRED]: value,
    }));
  }, []);

  const handleKeyChange = useCallback(
    (value: string) => {
      const original = initialStateRef.current;
      if (value === original.key) {
        setSubmissionSummaryData({ ...original });
      } else if (hideQaSecondStpAndComments) {
        setSubmissionSummaryData((prev) => ({
          ...prev,
          [SubmissionSummaryDataKeys.KEY]: value,
          [SubmissionSummaryDataKeys.POC]: "",
        }));
      } else {
        setSubmissionSummaryData((prev) => ({
          ...prev,
          [SubmissionSummaryDataKeys.KEY]: value,
          [SubmissionSummaryDataKeys.QA_FILLED_BY]: "",
          [SubmissionSummaryDataKeys.SECOND_STP_STATUS]: "",
          [SubmissionSummaryDataKeys.CASE_STATUS]: "",
          [SubmissionSummaryDataKeys.POC]: "",
          [SubmissionSummaryDataKeys.COMMENTS]: "",
          ...(showCallFields
            ? {
                [SubmissionSummaryDataKeys.CALL_REQUIRED]: "",
                [SubmissionSummaryDataKeys.CALL_REASON]: "",
              }
            : {}),
        }));
      }
    },
    [hideQaSecondStpAndComments, showCallFields],
  );

  const callFieldsValid =
    !showCallFields ||
    ((submissionSummaryData.callRequired === "yes" ||
      submissionSummaryData.callRequired === "no") &&
      (submissionSummaryData.callRequired !== "yes" ||
        submissionSummaryData.callReason.trim() !== ""));

  const isKeyFieldSatisfied =
    isKeyFieldOptional || submissionSummaryData.key.trim() !== "";

  const isFormValid = hideQaSecondStpAndComments
    ? isKeyFieldSatisfied && submissionSummaryData.poc !== "" && callFieldsValid
    : isKeyFieldSatisfied &&
      submissionSummaryData.poc !== "" &&
      submissionSummaryData.caseStatus !== "" &&
      callFieldsValid;

  return {
    submissionSummaryData,
    handleSubmissionSummaryDataChange,
    handleQaFilledByChange,
    handleSecondStpStatusChange,
    handleCallRequiredChange,
    handleKeyChange,
    isFormValid,
  };
};
